import { Injectable } from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {
  addDays,
  addHours,
  addMonths,
  differenceInDays,
  differenceInMinutes,
  differenceInWeeks,
  format,
  isAfter,
  isBefore,
  isEqual,
  isMonday,
  isTuesday,
  isWednesday,
  isThursday,
  isFriday,
  isSaturday,
  isSunday,
  isToday,
  isValid,
  lastDayOfMonth,
  parse,
  startOfDay,
  startOfMonth,
  getWeek,
  subDays,
  isWithinInterval,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
  parseISO,
} from 'date-fns';
import { InquiryDateResponse } from '../../../api';
import { BaseIntervalStatus } from '../types/base-interval-status.type';

export const BASE_DATE_SERVICE_FORMAT = 'yyyy-MM-dd';
export const BASE_DATE_CLIENT_FORMAT = 'dd.MM.yyyy';

/** Default maximum lease period (Höchstüberlassungsdauer) in months, used when none is configured. */
export const MAX_LEASE_PERIOD_MONTHS = 18;

export enum BaseDateFormat {
  DateServiceFormat = 'yyyy-MM-dd',
  DateClientFormat = 'dd.MM.yyyy',
}

@Injectable({ providedIn: 'root' })
export class BaseDateService {
  static isValid(date: string): boolean {
    return isValid(date);
  }

  static format(date: Date, formatPattern: string = BASE_DATE_CLIENT_FORMAT): string {
    return format(date, formatPattern);
  }

  static isBefore(date1: Date, date2: Date): boolean {
    return isBefore(date1, date2);
  }

  static isAfter(date1: Date, date2: Date): boolean {
    return isAfter(date1, date2);
  }

  static isEqualOrAfter(date1: Date, date2: Date, ignoreTime: boolean = false): boolean {
    return (
      isEqual(ignoreTime ? date1.setHours(0, 0, 0, 0) : date1, ignoreTime ? date2.setHours(0, 0, 0, 0) : date2) ||
      isAfter(date1, date2)
    );
  }

  static addMonths(date: Date, months: number): Date {
    return addMonths(date, months);
  }

  static addDays(date: Date, days: number): Date {
    return addDays(date, days);
  }

  static addHours(date: Date, hours: number): Date {
    return addHours(date, hours);
  }

  static isEqual(date1: Date, date2: Date): boolean {
    return isEqual(date1, date2);
  }

  static isToday(date: Date): boolean {
    return isToday(date);
  }

  static differenceInDays(date1: Date, date2: Date): number {
    return differenceInDays(date2, date1);
  }

  static differenceInWeeks(date1: Date, date2: Date): number {
    return differenceInWeeks(date2, date1);
  }

  static differenceInMinutes(date1: Date, date2: Date): number {
    return differenceInMinutes(date1, date2);
  }

  static startOfDay(date: Date): Date {
    return startOfDay(date);
  }

  static toDate(date: string, formatString = BASE_DATE_SERVICE_FORMAT): Date {
    return parse(date, formatString, new Date());
  }

  static toServerFormatString(date: string): string {
    return date?.indexOf('.') > -1 ? date.split('.').reverse().join('-') : date;
  }

  static formatExportTime(month: { id: number; value: string }, year: number, toLastDayOfMonth = false): string {
    const newDate = BaseDateService.toDate(`${year}-${month?.id + 1}-01`);

    return month && year
      ? format(toLastDayOfMonth ? lastDayOfMonth(newDate) : newDate, BaseDateFormat.DateServiceFormat)
      : '';
  }

  static getDateDaysAgo(days: number): Date {
    return subDays(new Date(), days);
  }

  static getPreviousDay(date?: Date): Date {
    const dateToCompare: Date = date ? date : new Date();
    const previousDay = new Date(dateToCompare.getTime());
    previousDay.setDate(dateToCompare.getDate() - 1);

    return previousDay;
  }

  static getNextDay(date?: Date): Date {
    const dateToCompare: Date = date ? date : new Date();
    const nextDay = new Date(dateToCompare.getTime());
    nextDay.setDate(dateToCompare.getDate() + 1);

    return nextDay;
  }

  static getDayOneWeekAgo(date?: Date): Date {
    const dateToCompare: Date = date ? date : new Date();
    const oneWeekAgoDay = new Date(dateToCompare.getTime());
    oneWeekAgoDay.setDate(dateToCompare.getDate() - 7);

    return oneWeekAgoDay;
  }

  static getStartDayOfLastWeek(): Date {
    const oneWeekAgo = new Date(new Date().getTime()).setDate(new Date().getDate() - 7);

    return startOfWeek(oneWeekAgo, { weekStartsOn: 1 });
  }

  static getStartOfCurrentWeek(): Date {
    return startOfWeek(new Date(), { weekStartsOn: 1 });
  }

  static getEndOfCurrentWeek(): Date {
    return endOfWeek(new Date(), { weekStartsOn: 1 });
  }

  static isMonday(datePositions: InquiryDateResponse[]) {
    return datePositions.findIndex((position) => isMonday(new Date(position.date))) !== -1;
  }

  static isTuesday(datePositions: InquiryDateResponse[]) {
    return datePositions.findIndex((position) => isTuesday(new Date(position.date))) !== -1;
  }

  static isWednesday(datePositions: InquiryDateResponse[]) {
    return datePositions.findIndex((position) => isWednesday(new Date(position.date))) !== -1;
  }

  static isThursday(datePositions: InquiryDateResponse[]) {
    return datePositions.findIndex((position) => isThursday(new Date(position.date))) !== -1;
  }

  static isFriday(datePositions: InquiryDateResponse[]) {
    return datePositions.findIndex((position) => isFriday(new Date(position.date))) !== -1;
  }

  static isSaturday(datePositions: InquiryDateResponse[]) {
    return datePositions.findIndex((position) => isSaturday(new Date(position.date))) !== -1;
  }

  static isSunday(datePositions: InquiryDateResponse[]) {
    return datePositions.findIndex((position) => isSunday(new Date(position.date))) !== -1;
  }

  /**
   * Convert a time string into a Date object from today with given
   * hours, minutes, seconds and milliseconds.
   * The given string needs to be in format HH:mm:ss:ms (e.g.: "18:25:15:999"), seperated by ":".
   * All parts that are missing from the end are replaced with a 0 value.
   * @param timeString
   */
  static setDateTimeByString(timeString: string): Date {
    const date = new Date();
    const time: number[] = timeString.split(':').map(Number);

    if (time.length >= 1) {
      date.setHours(time[0]);
      date.setMinutes(time[1] ? time[1] : 0);
      date.setSeconds(time[2] ? time[2] : 0);
      date.setMilliseconds(time[3] ? time[3] : 0);
    }

    return date;
  }

  static isBreakTimeWithinWorkingHours(
    breakStartString: string,
    breakEndString: string,
    workStartString: string,
    workEndString: string
  ): boolean {
    let breakStartDate = BaseDateService.setDateTimeByString(breakStartString);
    const breakStartNextDayDate = new Date(breakStartDate);
    breakStartNextDayDate.setDate(breakStartDate.getDate() + 1);
    let breakEndDate = BaseDateService.setDateTimeByString(breakEndString);
    const breakEndNextDayDate = new Date(breakEndDate);
    breakEndNextDayDate.setDate(breakEndDate.getDate() + 1);
    const workStartDate = BaseDateService.setDateTimeByString(workStartString);
    let workEndDate = BaseDateService.setDateTimeByString(workEndString);
    let workEndNextDayDate = new Date(workEndDate);
    workEndNextDayDate.setDate(workEndDate.getDate() + 1);

    // Check if the end time is on the next day and adjust it
    if (
      workEndDate < workStartDate &&
      breakStartDate < workStartDate &&
      breakStartString >= '00:00' &&
      breakStartString <= workEndString
    ) {
      breakStartDate = breakStartNextDayDate;
    }
    if (breakEndDate < breakStartDate) {
      breakEndDate = breakEndNextDayDate;
    }
    if (workEndDate < workStartDate) {
      workEndDate = workEndNextDayDate;
    }

    return breakStartDate >= workStartDate && breakEndDate <= workEndDate;
  }

  static isWeekendDay(date: Date) {
    if (!date) {
      return false;
    }

    return date.getDay() === 0 || date.getDay() === 6;
  }

  static weekNumber(date: string): number {
    return getWeek(new Date(date), { weekStartsOn: 1, firstWeekContainsDate: 4 });
  }

  static isWithinInterval(date: Date, interval: Interval): boolean {
    return isWithinInterval(date, interval);
  }

  static eachDayOfInterval(start: Date, end: Date): Date[] {
    return eachDayOfInterval({ start, end });
  }

  static convertServerFormatToNgbDateStruct(date: string): NgbDateStruct {
    const [year, month, day] = date.split('-').map((part) => parseInt(part, 10));
    return { year, month, day };
  }

  static toNgbDateStruct(date: Date): NgbDateStruct {
    return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
  }

  static getFirstDayOfPreviousMonth(referenceDate: Date = new Date()): NgbDateStruct {
    return BaseDateService.toNgbDateStruct(startOfMonth(addMonths(referenceDate, -1)));
  }

  static getLaterNgbDateStruct(date1: NgbDateStruct, date2: NgbDateStruct): NgbDateStruct {
    const asDate1 = new Date(date1.year, date1.month - 1, date1.day);
    const asDate2 = new Date(date2.year, date2.month - 1, date2.day);

    return isAfter(asDate1, asDate2) ? date1 : date2;
  }

  /**
   * Last day that is still covered by the maximum lease period (Höchstüberlassungsdauer).
   * The period end is inclusive, so it is start + N months - 1 day: a start of 2025-08-18
   * with an 18 month period ends on 2027-02-17, not 2027-02-18.
   * Unlike the Equal Pay date, which is the first day of its period and needs no offset.
   */
  static maxLeasePeriodEndDate(startDate: string, months: number | undefined): string {
    const start = parseISO(startDate);

    if (!isValid(start)) {
      throw new Error('Invalid dates');
    }

    const end = subDays(addMonths(start, months ? months : MAX_LEASE_PERIOD_MONTHS), 1);

    return format(end, BaseDateFormat.DateServiceFormat);
  }

  static getIntervalStatus(startDate: string, endDate: string) {
    const now = new Date();
    const start = parseISO(startDate);
    const end = parseISO(endDate);

    if (!isValid(start) || !isValid(end)) {
      throw new Error('Invalid dates');
    }

    if (isBefore(end, start)) {
      throw new Error('endDate must be on/after startDate');
    }

    if (isBefore(now, start)) {
      return BaseIntervalStatus.planned; // now < start
    }

    if (isBefore(now, end)) {
      return BaseIntervalStatus.active; // start <= now < end
    }

    return BaseIntervalStatus.ended;
  }

  static getOtherDatesInPeriod(startDate: string, endDate: string, dates: string[]): string[] {
    const start = parseISO(startDate);
    const end = parseISO(endDate);

    if (!isValid(start) || !isValid(end)) {
      throw new Error('Invalid dates');
    }
    if (isBefore(end, start)) {
      throw new Error('endDate must be on/after startDate');
    }

    const existing = new Set<string>(dates ?? []);
    return BaseDateService.eachDayOfInterval(start, end)
      .map((date) => BaseDateService.format(date, BaseDateFormat.DateServiceFormat))
      .filter((dateString) => !existing.has(dateString));
  }

  static countDaysNotInSet(startDate: string, endDate: string, excludedDates: Set<string>): number {
    const start = parseISO(startDate);
    const end = parseISO(endDate);

    if (!isValid(start) || !isValid(end)) {
      throw new Error('Invalid dates');
    }
    if (isBefore(end, start)) {
      throw new Error('endDate must be on/after startDate');
    }

    const allDaysFromInterval = BaseDateService.eachDayOfInterval(start, end);

    return allDaysFromInterval
      .map((date) => BaseDateService.format(date, BaseDateFormat.DateServiceFormat))
      .filter((dateString) => !excludedDates.has(dateString)).length;
  }

  // Hotfix solution to convert a date to Berlin time zone string
  // It is used for terminal status calculation until backend does not return a timezone info
  static nowInBerlinToServiceDateTimeString(): string {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('de-DE', {
      timeZone: 'Europe/Berlin',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(now);

    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';

    return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}`;
  }
}
